import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Tv, Trophy } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_META = {
  netflix:        { name: 'Netflix',       color: '#E50914' },
  hulu:           { name: 'Hulu',          color: '#1CE783' },
  disney_plus:    { name: 'Disney+',       color: '#113CCF' },
  hbo_max:        { name: 'HBO Max',       color: '#B535F6' },
  amazon_prime:   { name: 'Prime Video',   color: '#00A8E1' },
  apple_tv:       { name: 'Apple TV+',     color: '#aaaaaa' },
  peacock:        { name: 'Peacock',       color: '#FDB927' },
  paramount_plus: { name: 'Paramount+',    color: '#0064FF' },
  espn_plus:      { name: 'ESPN+',         color: '#FF0000' },
  crunchyroll:    { name: 'Crunchyroll',   color: '#F47521' },
};

const DNA_TYPES = {
  'Completionist': {
    emoji: '🏆',
    code: 'CP',
    tagline: 'You finish everything you start.',
    description: 'Cliffhangers don\'t scare you — they fuel you. You push through slow seasons, forgettable episodes, and bad finales. When you start something, you see it through.',
    color: '#facc15',
  },
  'Explorer': {
    emoji: '🗺️',
    code: 'EX',
    tagline: 'You\'ve seen it all.',
    description: 'Your watchlist is basically a streaming service itself. You\'re always three shows ahead of the algorithm, discovering gems before they trend.',
    color: '#4A9EFF',
  },
  'Selective': {
    emoji: '🎯',
    code: 'SL',
    tagline: 'Quality over quantity.',
    description: 'Every show on your list earned its spot. You don\'t watch anything just to watch something. Your recommendations carry weight.',
    color: '#a78bfa',
  },
  'Binge Watcher': {
    emoji: '🔥',
    code: 'BW',
    tagline: 'One episode is just the warm-up.',
    description: 'You watch in full-season runs. Sleep is optional when there\'s a good show on. Your "just one more" is never just one more.',
    color: '#f97316',
  },
  'Casual Viewer': {
    emoji: '😎',
    code: 'CV',
    tagline: 'You watch when the mood strikes.',
    description: 'No pressure, just vibes. You\'re not racing to finish anything. Streaming is relaxation, not a competition.',
    color: '#22c55e',
  },
};

// ---------------------------------------------------------------------------
// DnaHelix — animated double-helix visual.
// Replaces the big emoji in the hero. The base-pair rungs are colored by
// the user's actual service distribution, so the helix is a literal
// rendering of their viewing.
// ---------------------------------------------------------------------------
function DnaHelix({ services, accent }) {
  const W = 180;
  const H = 220;
  const amp = 46;
  const turns = 2.2;
  const rungCount = 16;

  // Build a weighted color list from the service distribution
  const serviceList = (services && services.length)
    ? services
    : [{ id: 'unknown', share: 1, color: '#666' }];

  const rungColors = [];
  for (let i = 0; i < rungCount; i++) {
    const t = i / rungCount;
    let acc = 0;
    let picked = serviceList[0].color;
    for (const s of serviceList) {
      acc += s.share;
      if (t <= acc) { picked = s.color; break; }
    }
    rungColors.push(picked);
  }

  // Strand path generators
  const strandPath = (phase) => {
    let d = '';
    const steps = 50;
    for (let i = 0; i <= steps; i++) {
      const y = (i / steps) * H;
      const angle = (i / steps) * Math.PI * 2 * turns + phase;
      const x = W / 2 + Math.sin(angle) * amp;
      d += (i === 0 ? 'M' : 'L') + x.toFixed(1) + ' ' + y.toFixed(1) + ' ';
    }
    return d;
  };

  const rungs = [];
  for (let i = 0; i < rungCount; i++) {
    const frac = (i + 0.5) / rungCount;
    const y = frac * H;
    const angle = frac * Math.PI * 2 * turns;
    const x1 = W / 2 + Math.sin(angle) * amp;
    const x2 = W / 2 + Math.sin(angle + Math.PI) * amp;
    const depth = (Math.cos(angle) + 1) / 2;
    rungs.push({ x1, x2, y, color: rungColors[i], depth, i });
  }

  return (
    <svg
      width={W}
      height={H}
      viewBox={`0 0 ${W} ${H}`}
      style={{ display: 'block', margin: '0 auto' }}
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="qdna-strandgrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={accent} stopOpacity="0.25" />
          <stop offset="50%" stopColor={accent} stopOpacity="1" />
          <stop offset="100%" stopColor={accent} stopOpacity="0.25" />
        </linearGradient>
      </defs>

      {/* base-pair rungs */}
      {rungs.map((r) => (
        <line
          key={'rung' + r.i}
          x1={r.x1}
          y1={r.y}
          x2={r.x2}
          y2={r.y}
          stroke={r.color}
          strokeWidth={1.4 + r.depth * 2}
          strokeOpacity={0.4 + r.depth * 0.55}
          strokeLinecap="round"
          className="qdna-rung"
          style={{ animationDelay: `${0.3 + r.i * 0.05}s` }}
        />
      ))}

      {/* the two strands */}
      <path
        d={strandPath(0)}
        fill="none"
        stroke="url(#qdna-strandgrad)"
        strokeWidth="2.5"
        strokeLinecap="round"
        className="qdna-strand"
      />
      <path
        d={strandPath(Math.PI)}
        fill="none"
        stroke="url(#qdna-strandgrad)"
        strokeWidth="2.5"
        strokeLinecap="round"
        className="qdna-strand qdna-strand-2"
      />

      {/* nodes where rungs meet strands */}
      {rungs.map((r) => (
        <g key={'node' + r.i}>
          <circle
            cx={r.x1}
            cy={r.y}
            r={1.8 + r.depth * 1.8}
            fill={r.color}
            className="qdna-node"
            style={{ animationDelay: `${0.3 + r.i * 0.05}s` }}
          />
          <circle
            cx={r.x2}
            cy={r.y}
            r={1.8 + (1 - r.depth) * 1.8}
            fill={r.color}
            className="qdna-node"
            style={{ animationDelay: `${0.3 + r.i * 0.05}s` }}
          />
        </g>
      ))}
    </svg>
  );
}

// ---------------------------------------------------------------------------
// WeekRhythm — 7 columns, height by relative volume
// ---------------------------------------------------------------------------
function WeekRhythm({ byDay, accent }) {
  const days = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
  const data = (byDay && byDay.length === 7) ? byDay : [0, 0, 0, 0, 0, 0, 0];
  const max = Math.max(1, ...data);
  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end', height: 90 }}>
      {data.map((v, i) => (
        <div key={i} style={{
          flex: 1, display: 'flex', flexDirection: 'column',
          alignItems: 'center', height: '100%',
        }}>
          <div style={{ flex: 1, width: '100%', display: 'flex', alignItems: 'flex-end' }}>
            <div
              className="qdna-week-bar"
              style={{
                width: '100%',
                height: Math.max(4, (v / max) * 100) + '%',
                background: accent,
                borderRadius: '4px 4px 2px 2px',
                animationDelay: 0.2 + i * 0.05 + 's',
              }}
            />
          </div>
          <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', marginTop: 6 }}>
            {days[i]}
          </span>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// DayClock — 24-hour radial chart showing when the user watches
// ---------------------------------------------------------------------------
function DayClock({ byHour, accent }) {
  const data = (byHour && byHour.length === 24) ? byHour : new Array(24).fill(0);
  const max = Math.max(1, ...data);
  const size = 130;
  const cx = size / 2;
  const cy = size / 2;
  const inner = 24;
  const outer = 56;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ display: 'block', margin: '0 auto' }} aria-hidden="true">
      {data.map((v, h) => {
        const a0 = (h / 24) * Math.PI * 2 - Math.PI / 2;
        const a1 = ((h + 1) / 24) * Math.PI * 2 - Math.PI / 2;
        const r = inner + (v / max) * (outer - inner);
        const x0i = cx + Math.cos(a0) * inner;
        const y0i = cy + Math.sin(a0) * inner;
        const x0o = cx + Math.cos(a0) * r;
        const y0o = cy + Math.sin(a0) * r;
        const x1o = cx + Math.cos(a1) * r;
        const y1o = cy + Math.sin(a1) * r;
        const x1i = cx + Math.cos(a1) * inner;
        const y1i = cy + Math.sin(a1) * inner;
        const d = `M${x0i} ${y0i} L${x0o} ${y0o} A${r} ${r} 0 0 1 ${x1o} ${y1o} L${x1i} ${y1i} Z`;
        return (
          <path
            key={h}
            d={d}
            fill={accent}
            fillOpacity={0.15 + (v / max) * 0.8}
            className="qdna-clock-seg"
            style={{ animationDelay: 0.2 + h * 0.02 + 's' }}
          />
        );
      })}
      <circle cx={cx} cy={cy} r={inner - 3} fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="1" />
      <text x={cx} y={cy - outer - 3} textAnchor="middle" style={{ fontSize: 8, fill: 'rgba(255,255,255,0.35)', fontFamily: 'monospace' }}>12a</text>
      <text x={cx + outer + 7} y={cy + 3} textAnchor="middle" style={{ fontSize: 8, fill: 'rgba(255,255,255,0.35)', fontFamily: 'monospace' }}>6a</text>
      <text x={cx} y={cy + outer + 11} textAnchor="middle" style={{ fontSize: 8, fill: 'rgba(255,255,255,0.35)', fontFamily: 'monospace' }}>12p</text>
      <text x={cx - outer - 7} y={cy + 3} textAnchor="middle" style={{ fontSize: 8, fill: 'rgba(255,255,255,0.35)', fontFamily: 'monospace' }}>6p</text>
    </svg>
  );
}

function CircleProgress({ percent, color = '#7c3aed' }) {
  const radius = 54;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (percent / 100) * circumference;
  return (
    <div style={{ position: 'relative', width: 140, height: 140, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <svg width="140" height="140" style={{ transform: 'rotate(-90deg)', position: 'absolute' }}>
        <circle cx="70" cy="70" r={radius} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="10" />
        <circle cx="70" cy="70" r={radius} fill="none"
          stroke="url(#dnaGrad)" strokeWidth="10"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 1.2s ease' }}
        />
        <defs>
          <linearGradient id="dnaGrad" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#7c3aed" />
            <stop offset="100%" stopColor="#4A9EFF" />
          </linearGradient>
        </defs>
      </svg>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <span style={{ fontSize: 32, fontWeight: 900, color: '#fff', lineHeight: 1 }}>{percent}%</span>
        <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginTop: 2 }}>finished</span>
      </div>
    </div>
  );
}

function ServiceBar({ serviceId, count, max }) {
  const meta = SERVICE_META[serviceId] || { name: serviceId, color: '#666' };
  const pct = max > 0 ? (count / max) * 100 : 0;
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: '#fff' }}>{meta.name}</span>
        <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>{count} show{count !== 1 ? 's' : ''}</span>
      </div>
      <div style={{ background: 'rgba(255,255,255,0.06)', borderRadius: 4, height: 6 }}>
        <div style={{
          height: 6, borderRadius: 4,
          background: meta.color,
          width: `${pct}%`,
          transition: 'width 1s ease',
        }} />
      </div>
    </div>
  );
}

export default function WatchDNA() {
  const { user, getToken } = useAuth();
  const [dna, setDna] = useState(null);
  const [shows, setShows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = await getToken();
        const headers = { Authorization: `Bearer ${token}` };
        const [dnaRes, showsRes] = await Promise.all([
          axios.get(`${API}/watch-dna`, { headers }),
          axios.get(`${API}/shows`, { headers }),
        ]);
        setDna(dnaRes.data);
        setShows(showsRes.data);
      } catch (err) {
        console.error('Failed to fetch Watch DNA:', err);
      } finally {
        setLoading(false);
      }
    };
    if (user) fetchData();
  }, [user, getToken]);

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300 }}>
        <div style={{ width: 24, height: 24, border: '2px solid #7c3aed', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (!dna) {
    return (
      <div style={{ textAlign: 'center', padding: '64px 20px', color: 'rgba(255,255,255,0.3)' }}>
        Could not load your Watch DNA. Try again later.
      </div>
    );
  }

  const dnaType = DNA_TYPES[dna.dna_label] || DNA_TYPES['Casual Viewer'];
  const topServiceMeta = dna.top_service ? SERVICE_META[dna.top_service] : null;

  // Build service breakdown from shows
  const serviceCounts = {};
  shows.forEach(show => {
    if (show.service_id) {
      serviceCounts[show.service_id] = (serviceCounts[show.service_id] || 0) + 1;
    }
  });
  const sortedServices = Object.entries(serviceCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5);
  const maxServiceCount = sortedServices[0]?.[1] || 1;

  // Service share array used by the DNA helix to color base pairs
  const totalServiceCount = Object.values(serviceCounts).reduce((a, b) => a + b, 0) || 1;
  const serviceShares = sortedServices.map(([id, count]) => ({
    id,
    share: count / totalServiceCount,
    color: (SERVICE_META[id] && SERVICE_META[id].color) || '#666',
  }));

  // Recent sessions
  const recentSessions = dna.recent_sessions || [];

  // Watching vs finished breakdown
  const finishedShows = shows.filter(s => s.status === 'watched');
  const watchingShows = shows.filter(s => s.status === 'watching');
  const notStarted = shows.filter(s => s.status !== 'watched' && s.status !== 'watching');

  // Optional new backend fields (gracefully absent if backend hasn't been updated)
  const byDay  = dna.viewing_by_day  || null;   // expect array of 7 numbers, Sun..Sat
  const byHour = dna.viewing_by_hour || null;   // expect array of 24 numbers, 0..23

  // DNA sequence code -- shareable signature like "BW-7342"
  const sequenceSig = (
    (dna.completion_rate || 0) * 97 +
    (dna.total_shows || 0) * 13 +
    (dna.watched || 0) * 31 +
    (dna.total_sessions || 0) * 7
  ) % 10000;
  const dnaSequence = `${dnaType.code}-${String(Math.floor(sequenceSig)).padStart(4, '0')}`;

  return (
    <div style={{ maxWidth: 480, margin: '0 auto', paddingBottom: 40 }}>

      {/* Header */}
      <div style={{ marginBottom: 24, paddingTop: 4 }}>
        <h1 style={{ fontSize: 28, fontWeight: 900, color: '#fff', margin: '0 0 4px' }}>
          Watch DNA 🧬
        </h1>
        <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.35)', margin: 0 }}>
          Your streaming personality, decoded.
        </p>
      </div>

      {/* DNA Type Hero — emoji replaced with animated helix */}
      <div style={{
        borderRadius: 24, overflow: 'hidden', marginBottom: 20,
        background: 'linear-gradient(135deg, #1a0a2e, #0a1a2e)',
        border: `1px solid ${dnaType.color}30`,
        padding: '28px 24px',
        position: 'relative', textAlign: 'center',
      }}>
        <div style={{
          position: 'absolute', inset: 0, opacity: 0.15,
          background: `radial-gradient(ellipse at center, ${dnaType.color} 0%, transparent 70%)`,
        }} />
        <div style={{ position: 'relative' }}>
          <div style={{ marginBottom: 14 }}>
            <DnaHelix services={serviceShares} accent={dnaType.color} />
          </div>
          <p style={{ fontSize: 11, fontWeight: 800, color: dnaType.color, letterSpacing: '0.1em', textTransform: 'uppercase', margin: '0 0 6px' }}>
            Your Type
          </p>
          <h2 style={{ fontSize: 32, fontWeight: 900, color: '#fff', margin: '0 0 8px', lineHeight: 1.1 }}>
            The {dna.dna_label}
          </h2>
          <p style={{ fontSize: 14, color: dnaType.color, fontWeight: 600, margin: '0 0 12px' }}>
            "{dnaType.tagline}"
          </p>
          <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)', margin: '0 0 16px', lineHeight: 1.6, maxWidth: 320, marginLeft: 'auto', marginRight: 'auto' }}>
            {dnaType.description}
          </p>
          {/* Shareable DNA sequence code */}
          <div style={{
            display: 'inline-flex', flexDirection: 'column', gap: 3,
            padding: '8px 16px',
            background: 'rgba(0,0,0,0.35)',
            border: `1px solid ${dnaType.color}25`,
            borderRadius: 8,
          }}>
            <span style={{ fontSize: 9, letterSpacing: '0.18em', color: 'rgba(255,255,255,0.4)', fontWeight: 700 }}>
              DNA SEQUENCE
            </span>
            <span style={{
              fontFamily: '"SF Mono", "Roboto Mono", Menlo, Consolas, monospace',
              fontSize: 15, fontWeight: 700, letterSpacing: '0.08em',
              color: '#fff',
            }}>
              {dnaSequence}
            </span>
          </div>
        </div>
      </div>

      {/* Completion + Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
        <div style={{
          borderRadius: 20, border: '1px solid rgba(255,255,255,0.06)',
          background: '#0a0a0a', padding: '20px',
          display: 'flex', flexDirection: 'column', alignItems: 'center',
        }}>
          <CircleProgress percent={dna.completion_rate} />
          <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', margin: '10px 0 0', textAlign: 'center' }}>
            Completion Rate
          </p>
        </div>
        <div style={{
          borderRadius: 20, border: '1px solid rgba(255,255,255,0.06)',
          background: '#0a0a0a', padding: '20px',
          display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 12,
        }}>
          {[
            { value: dna.total_shows, label: 'Shows Tracked', color: '#a78bfa' },
            { value: dna.watched, label: 'Finished', color: '#22c55e' },
            { value: dna.watching, label: 'In Progress', color: '#4A9EFF' },
            { value: dna.total_sessions, label: 'Sessions', color: '#f97316' },
          ].map((item, i) => (
            <div key={i}>
              {i > 0 && <div style={{ height: 1, background: 'rgba(255,255,255,0.05)', marginBottom: 12 }} />}
              <p style={{ fontSize: 20, fontWeight: 900, color: item.color, margin: 0, lineHeight: 1 }}>{item.value}</p>
              <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', margin: '2px 0 0' }}>{item.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* When You Watch — renders only if backend provides the data */}
      {(byDay || byHour) && (
        <div style={{
          borderRadius: 20, border: '1px solid rgba(255,255,255,0.06)',
          background: '#0a0a0a', padding: '16px', marginBottom: 16,
        }}>
          <p style={{ fontSize: 11, fontWeight: 800, color: 'rgba(255,255,255,0.4)', letterSpacing: '0.08em', textTransform: 'uppercase', margin: '0 0 14px' }}>
            When You Watch
          </p>
          <div style={{
            display: 'grid',
            gridTemplateColumns: byDay && byHour ? '1fr 1fr' : '1fr',
            gap: 20,
            alignItems: 'center',
          }}>
            {byDay && (
              <div>
                <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', margin: '0 0 10px', textAlign: 'center' }}>
                  Across the week
                </p>
                <WeekRhythm byDay={byDay} accent={dnaType.color} />
              </div>
            )}
            {byHour && (
              <div>
                <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', margin: '0 0 10px', textAlign: 'center' }}>
                  Across the day
                </p>
                <DayClock byHour={byHour} accent={dnaType.color} />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Watchlist Breakdown */}
      <div style={{
        borderRadius: 20, border: '1px solid rgba(255,255,255,0.06)',
        background: '#0a0a0a', padding: '16px', marginBottom: 16,
      }}>
        <p style={{ fontSize: 11, fontWeight: 800, color: 'rgba(255,255,255,0.4)', letterSpacing: '0.08em', textTransform: 'uppercase', margin: '0 0 14px' }}>
          Watchlist Breakdown
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
          {[
            { value: finishedShows.length, label: 'Finished', color: '#22c55e', emoji: '✅' },
            { value: watchingShows.length, label: 'Watching', color: '#4A9EFF', emoji: '▶️' },
            { value: notStarted.length, label: 'Not Started', color: '#a78bfa', emoji: '📋' },
          ].map((item, i) => (
            <div key={i} style={{
              borderRadius: 14, background: `${item.color}10`,
              border: `1px solid ${item.color}25`, padding: '12px 8px', textAlign: 'center',
            }}>
              <div style={{ fontSize: 20, marginBottom: 4 }}>{item.emoji}</div>
              <p style={{ fontSize: 18, fontWeight: 900, color: item.color, margin: 0 }}>{item.value}</p>
              <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', margin: '2px 0 0' }}>{item.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Service Breakdown */}
      {sortedServices.length > 0 && (
        <div style={{
          borderRadius: 20, border: '1px solid rgba(255,255,255,0.06)',
          background: '#0a0a0a', padding: '16px', marginBottom: 16,
        }}>
          <p style={{ fontSize: 11, fontWeight: 800, color: 'rgba(255,255,255,0.4)', letterSpacing: '0.08em', textTransform: 'uppercase', margin: '0 0 14px' }}>
            Service Breakdown
          </p>
          {sortedServices.map(([serviceId, count]) => (
            <ServiceBar key={serviceId} serviceId={serviceId} count={count} max={maxServiceCount} />
          ))}
        </div>
      )}

      {/* Top Service highlight */}
      {topServiceMeta && (
        <div style={{
          borderRadius: 20, border: `1px solid ${topServiceMeta.color}30`,
          background: `${topServiceMeta.color}08`, padding: '16px',
          display: 'flex', alignItems: 'center', gap: 14, marginBottom: 16,
        }}>
          <div style={{
            width: 48, height: 48, borderRadius: 14, flexShrink: 0,
            background: `${topServiceMeta.color}20`, border: `1px solid ${topServiceMeta.color}40`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Trophy size={22} style={{ color: topServiceMeta.color }} />
          </div>
          <div>
            <p style={{ fontSize: 10, fontWeight: 800, color: 'rgba(255,255,255,0.4)', letterSpacing: '0.08em', textTransform: 'uppercase', margin: '0 0 2px' }}>
              Favourite Service
            </p>
            <p style={{ fontSize: 18, fontWeight: 900, color: '#fff', margin: 0 }}>{topServiceMeta.name}</p>
          </div>
        </div>
      )}

      {/* Recent Sessions */}
      {recentSessions.length > 0 && (
        <div style={{
          borderRadius: 20, border: '1px solid rgba(255,255,255,0.06)',
          background: '#0a0a0a', padding: '16px', marginBottom: 16,
        }}>
          <p style={{ fontSize: 11, fontWeight: 800, color: 'rgba(255,255,255,0.4)', letterSpacing: '0.08em', textTransform: 'uppercase', margin: '0 0 14px' }}>
            Recent Activity
          </p>
          {recentSessions.slice(0, 5).map((session, i) => {
            const svc = SERVICE_META[session.service_id] || {};
            const date = session.started_at
              ? new Date(session.started_at).toLocaleDateString([], { month: 'short', day: 'numeric' })
              : '';
            return (
              <div key={session.session_id || i} style={{
                display: 'flex', alignItems: 'center', gap: 10,
                paddingBottom: i < recentSessions.length - 1 ? 10 : 0,
                marginBottom: i < recentSessions.length - 1 ? 10 : 0,
                borderBottom: i < recentSessions.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none',
              }}>
                <div style={{
                  width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                  background: svc.color || '#666',
                }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: 12, fontWeight: 600, color: '#fff', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {svc.name || session.service_id || 'Unknown'}
                  </p>
                </div>
                <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', flexShrink: 0 }}>{date}</p>
              </div>
            );
          })}
        </div>
      )}

      {/* CTA if no shows */}
      {dna.total_shows === 0 && (
        <div style={{
          borderRadius: 20, border: '1px solid rgba(255,255,255,0.06)',
          background: '#0a0a0a', padding: '32px 20px', textAlign: 'center',
        }}>
          <Tv size={36} style={{ color: '#333', display: 'block', margin: '0 auto 12px' }} />
          <p style={{ color: '#fff', fontWeight: 700, margin: '0 0 6px' }}>No data yet</p>
          <p style={{ color: 'rgba(255,255,255,0.35)', fontSize: 13, margin: '0 0 16px' }}>
            Start watching and your DNA will build up automatically
          </p>
          <Link to="/watchlist">
            <button style={{
              background: 'linear-gradient(135deg, #7c3aed, #4A9EFF)',
              border: 'none', borderRadius: 20, padding: '9px 20px',
              fontSize: 13, fontWeight: 700, color: '#fff', cursor: 'pointer',
            }}>
              Add a Show
            </button>
          </Link>
        </div>
      )}

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        .qdna-strand {
          stroke-dasharray: 900;
          stroke-dashoffset: 900;
          animation: qdna-draw 1.3s ease 0.1s forwards;
        }
        .qdna-strand-2 { animation-delay: 0.25s; }
        @keyframes qdna-draw { to { stroke-dashoffset: 0; } }
        .qdna-rung, .qdna-node { opacity: 0; animation: qdna-fade 0.4s ease forwards; }
        @keyframes qdna-fade { to { opacity: 1; } }
        .qdna-week-bar {
          animation: qdna-rise-bar 0.6s cubic-bezier(.22,1,.36,1) both;
        }
        @keyframes qdna-rise-bar { from { height: 0 !important; } }
        .qdna-clock-seg { opacity: 0; animation: qdna-fade 0.4s ease forwards; }
      `}</style>
    </div>
  );
}
